import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageTransition } from '../transitions/PageTransition';
import { useCursorContext } from '../../hooks/useCursorContext';

export function LievyTermsPage() {
  const { setCursorState } = useCursorContext();

  return (
    <PageTransition>
      <div className="min-h-screen bg-[#0a0a0a] pt-32 pb-20">
        <div className="fixed top-24 left-8 md:left-12 z-40">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/40 hover:text-emerald-500 transition-colors group"
            onMouseEnter={() => setCursorState('hovering')}
            onMouseLeave={() => setCursorState('default')}
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-xs tracking-wider uppercase">Zurück</span>
          </Link>
        </div>

        <div className="max-w-3xl mx-auto px-8 md:px-12">
          <h1 className="text-4xl md:text-5xl font-light text-white mb-4">
            Nutzungsbedingungen
          </h1>
          <p className="text-white/40 mb-12">Lievy — Date Night App</p>

          <div className="space-y-10 text-white/70">
            <section>
              <h2 className="text-lg text-white mb-4">1. Geltungsbereich</h2>
              <p className="leading-relaxed">
                Diese Nutzungsbedingungen gelten für die Nutzung der iOS-App "Lievy"
                (nachfolgend "App"), bereitgestellt von Niko Knez / NK Design,
                Köln-Aachener-Straße 155, 50127 Bergheim. Mit der Nutzung der App
                akzeptierst du diese Bedingungen.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">2. Beschreibung der App</h2>
              <p className="leading-relaxed">
                Lievy ist eine App für Paare, die gemeinsame Date-Ideen entdecken,
                planen und festhalten möchten. Die App bietet kostenlose Basisfunktionen
                sowie ein optionales Premium-Abonnement ("Lievy Plus") mit erweiterten Features.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">3. Registrierung & Account</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Du musst mindestens 16 Jahre alt sein, um Lievy zu nutzen.</li>
                <li>Du bist verantwortlich für die Sicherheit deines Accounts.</li>
                <li>Falsche Angaben können zur Sperrung führen.</li>
                <li>Pro Person ist ein Account erlaubt.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">4. Lievy Plus (Premium-Abo)</h2>
              <p className="leading-relaxed mb-4">
                Lievy Plus ist ein optionales Auto-Renewable Subscription.
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong className="text-white">Preise:</strong> Monatlich 7,99 € oder Jährlich 59,99 € (inkl. MwSt.)</li>
                <li><strong className="text-white">Kostenlose Testphase:</strong> 7 Tage für neue Abonnenten</li>
                <li><strong className="text-white">Verlängerung:</strong> Das Abo verlängert sich automatisch, sofern es nicht mindestens 24 Stunden vor Ende des aktuellen Zeitraums gekündigt wird.</li>
                <li><strong className="text-white">Kündigung:</strong> Jederzeit über die App-Store-Einstellungen auf deinem Gerät möglich.</li>
                <li><strong className="text-white">Zahlung:</strong> Wird über deinen Apple-Account abgewickelt. Wir haben keinen Zugriff auf deine Zahlungsdaten.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">5. Verhaltensregeln</h2>
              <p className="leading-relaxed mb-4">
                Du verpflichtest dich:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Keine beleidigenden, diskriminierenden oder illegalen Inhalte hochzuladen</li>
                <li>Die App nicht für kommerzielle Zwecke zu missbrauchen</li>
                <li>Keine automatisierten Zugriffe (Bots, Scraper) zu verwenden</li>
                <li>Die Rechte anderer Nutzer zu respektieren</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">6. Geistiges Eigentum</h2>
              <p className="leading-relaxed">
                Alle Inhalte der App (Design, Texte, Illustrationen, Code) sind urheberrechtlich
                geschützt und Eigentum von NK Design. Die Fuchs-Charaktere "Lumi" und "Foxo"
                sind geschützte Markenzeichen. Eine Nutzung außerhalb der App ist ohne
                schriftliche Genehmigung nicht gestattet.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">7. Haftungsausschluss</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Wir garantieren keine ununterbrochene Verfügbarkeit der App.</li>
                <li>Date-Ideen sind Vorschläge — wir übernehmen keine Haftung für die tatsächliche Durchführung.</li>
                <li>Für von Nutzern hochgeladene Inhalte (Fotos, Texte) sind die Nutzer selbst verantwortlich.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">8. Kündigung & Account-Löschung</h2>
              <p className="leading-relaxed">
                Du kannst deinen Account jederzeit in der App löschen
                (Profil → Einstellungen → Account löschen). Bei Verstößen gegen diese
                Bedingungen behalten wir uns das Recht vor, Accounts zu sperren oder zu löschen.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">9. Änderungen</h2>
              <p className="leading-relaxed">
                Wir behalten uns vor, diese Nutzungsbedingungen anzupassen.
                Bei wesentlichen Änderungen informieren wir dich per In-App-Benachrichtigung.
                Die weitere Nutzung der App nach Änderung gilt als Zustimmung.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">10. Anwendbares Recht</h2>
              <p className="leading-relaxed">
                Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist Köln.
              </p>
            </section>

            <section>
              <h2 className="text-lg text-white mb-4">11. Kontakt</h2>
              <p className="leading-relaxed">
                Niko Knez / NK Design<br />
                E-Mail: niko@design-nk.de
              </p>
            </section>

            <section>
              <p className="text-white/40 text-sm">
                Stand: März 2026
              </p>
            </section>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
